import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Switch,
  Button,
  Autocomplete,
  Tooltip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { formatFirestoreDate } from '../utils/dateUtils';
import CoverUpload from '../components/CoverUpload';
import { productsService, Product, publishersService, Publisher, Title, titlesService } from '../services/firebaseFunctions';
import { useTitlePagination } from '../hooks';
import { useAuth } from '../contexts/AuthContext';
import { operationErrorMessage } from '../utils/adminFeedback';

type WithoutPublisherOption = { id: 'without-publisher'; name: 'Sem editora'; isWithoutPublisher: true };
type PublisherFilterOption = Publisher | WithoutPublisherOption;

const withoutPublisherOption: WithoutPublisherOption = {
  id: 'without-publisher',
  name: 'Sem editora',
  isWithoutPublisher: true,
};

const isWithoutPublisherOption = (option: PublisherFilterOption): option is WithoutPublisherOption => (
  'isWithoutPublisher' in option && option.isWithoutPublisher
);

const sameStringArray = (left: string[] | undefined, right: string[]): boolean => (
  (left ?? []).length === right.length && (left ?? []).every((value, index) => value === right[index])
);

const TitlesPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('titulos', 'editar');
  const page = useTitlePagination();
  const [publishers, setPublishers] = useState<Publisher[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTitle, setEditingTitle] = useState<Title | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    pages: '',
    tags: [] as string[],
    description: '',
    coverUrl: '',
    coverStoragePath: '',
    authors: [] as string[],
    isActive: true,
    publisherId: '' as string,
    productId: '' as string,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [submitting, setSubmitting] = useState(false);
  const [deletingTitleId, setDeletingTitleId] = useState<string | null>(null);
  const [titlePendingDeletion, setTitlePendingDeletion] = useState<Title | null>(null);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const publisherFilterOptions: PublisherFilterOption[] = [withoutPublisherOption, ...publishers];
  const selectedPublisherFilters = publisherFilterOptions.filter((publisher) => (
    isWithoutPublisherOption(publisher)
      ? page.includeWithoutPublisher
      : page.publisherIds.includes(publisher.id)
  ));

  const eligibleProducts = editingTitle ? products.filter((product) => product.tipo === editingTitle.type) : [];
  const selectedProductId = eligibleProducts.some((product) => product.id === formData.productId) ? formData.productId : '';

  // Publishers são necessários apenas para o diálogo de edição.
  useEffect(() => {
    void publishersService.getAll().then(setPublishers).catch(() => setPublishers([]));
  }, []);

  useEffect(() => {
    void productsService.getAll().then(setProducts).catch(() => setProducts([]));
  }, []);

  const handleOpenDialog = (title?: Title) => {
    if (title) {
      setEditingTitle(title);
      setFormData({
        name: title.name || '',
        pages: title.type === 'ebook' ? title.pages?.toString() || '' : '',
        tags: title.tags || [],
        description: title.description || '',
        coverUrl: title.coverUrl || '',
        coverStoragePath: title.coverStoragePath || '',
        authors: title.authors || [],
        isActive: title.isActive,
        publisherId: title.publisherId || '',
        productId: title.productId || '',
      });
    } else {
      setEditingTitle(null);
      setFormData({
        name: '',
        pages: '',
        tags: [],
        description: '',
        coverUrl: '',
        coverStoragePath: '',
        authors: [],
        isActive: true,
        publisherId: '',
        productId: '',
      });
    }
    setOpenDialog(true);
  };

  const resetTitleDialog = () => {
    setOpenDialog(false);
    setEditingTitle(null);
  };

  const handleCloseDialog = () => {
    if (submitting) return;
    resetTitleDialog();
  };

  const handleCoverChange = (coverUrl: string | null, storagePath?: string) => {
    setFormData((prev) => ({
      ...prev,
      coverUrl: coverUrl || '',
      coverStoragePath: coverUrl ? storagePath || '' : '',
    }));
  };

  const handleSubmit = async () => {
    if (!editingTitle) return;

    const name = formData.name.trim();
    if (name.length < 2) {
      setSnackbar({ open: true, message: 'O nome do título deve ter ao menos 2 caracteres.', severity: 'error' });
      return;
    }

    try {
      setSubmitting(true);
      const updateData: Parameters<typeof titlesService.update>[1] = {};

      if (name !== editingTitle.name.trim()) {
        updateData.name = name;
      }

      if (formData.description !== (editingTitle.description || '')) {
        updateData.description = formData.description.trim() === '' ? null : formData.description;
      }
      if (formData.coverUrl !== (editingTitle.coverUrl || '')) {
        updateData.coverUrl = formData.coverUrl.trim() === '' ? null : formData.coverUrl;
      }
      if (formData.coverStoragePath !== (editingTitle.coverStoragePath || '')) {
        updateData.coverStoragePath = formData.coverStoragePath.trim() === '' ? null : formData.coverStoragePath;
      }
      if (!sameStringArray(editingTitle.authors, formData.authors)) {
        updateData.authors = formData.authors.length === 0 ? null : formData.authors;
      }
      if (!sameStringArray(editingTitle.tags, formData.tags)) {
        updateData.tags = formData.tags.length === 0 ? null : formData.tags;
      }
      if (editingTitle.type === 'ebook') {
        const originalPages = editingTitle.pages?.toString() || '';
        if (formData.pages !== originalPages) {
          updateData.pages = formData.pages.trim() === '' ? null : Number.parseInt(formData.pages, 10);
        }
      }
      if (formData.publisherId !== (editingTitle.publisherId || '')) {
        updateData.publisherId = null;
        if (formData.publisherId) {
          updateData.publisherId = formData.publisherId;
        }
      }
      if (formData.productId !== (editingTitle.productId || '')) {
        updateData.productId = formData.productId || null;
      }
      if (formData.isActive !== editingTitle.isActive) {
        updateData.isActive = formData.isActive;
      }

      const data = await titlesService.update(editingTitle.id, updateData);

      if (data.success) {
        await page.refresh();

        setSnackbar({
          open: true,
          message: 'Título atualizado com sucesso!',
          severity: 'success',
        });
        resetTitleDialog();
      } else {
        setSnackbar({ open: true, message: operationErrorMessage('title', data), severity: 'error' });
      }
    } catch {
      setSnackbar({
        open: true,
        message: operationErrorMessage('title'),
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetDeleteDialog = () => {
    setTitlePendingDeletion(null);
    setDeleteConfirmed(false);
  };

  const handleOpenDeleteDialog = (title: Title) => {
    setTitlePendingDeletion(title);
    setDeleteConfirmed(false);
  };

  const handleCloseDeleteDialog = () => {
    if (deletingTitleId) return;
    resetDeleteDialog();
  };

  const handleDelete = async () => {
    if (!titlePendingDeletion || !deleteConfirmed || deletingTitleId) return;

    try {
      setDeletingTitleId(titlePendingDeletion.id);
      const data = await titlesService.delete(titlePendingDeletion.id);

      if (data.success) {
        await page.refresh();
        setSnackbar({
          open: true,
          message: 'Título excluído com sucesso!',
          severity: 'success',
        });
        resetDeleteDialog();
      } else {
        setSnackbar({ open: true, message: operationErrorMessage('title', data), severity: 'error' });
      }
    } catch {
      setSnackbar({
        open: true,
        message: operationErrorMessage('title'),
        severity: 'error',
      });
    } finally {
      setDeletingTitleId(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date: any): string => {
    return formatFirestoreDate(date);
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'audiobook':
        return 'Audiobook';
      case 'ebook':
        return 'E-book';
      case 'unknown':
        return 'Desconhecido';
      default:
        return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'audiobook':
        return 'secondary';
      case 'ebook':
        return 'primary';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Títulos ({page.totalCount})
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Filtrar por nome"
          value={page.search}
          onChange={(event) => page.setSearch(event.target.value)}
        />
        <Autocomplete
          multiple
          size="small"
          options={publisherFilterOptions}
          value={selectedPublisherFilters}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.name}
          onChange={(_, values) => {
            page.setPublisherIds(values.filter((option) => !isWithoutPublisherOption(option)).map((option) => option.id));
            page.setIncludeWithoutPublisher(values.some(isWithoutPublisherOption));
          }}
          sx={{ minWidth: 300, flex: 1 }}
          renderInput={(params) => <TextField {...params} label="Editoras" placeholder="Selecione editoras" />}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="title-page-size-label">Itens por página</InputLabel>
          <Select
            labelId="title-page-size-label"
            label="Itens por página"
            value={page.pageSize}
            onChange={(event) => page.setPageSize(Number(event.target.value))}
          >
            {[10, 30, 50, 100].map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {page.error && <Alert severity="error" sx={{ mb: 2 }}>{page.error}</Alert>}

      {!page.loading && page.items.length === 0 ? (
        <Alert severity="info">Nenhum título encontrado. Faça upload de arquivos na aba "Upload" para começar.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Tamanho</TableCell>
                <TableCell>Ativo</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Criado</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {page.loading ? (
                <TableRow><TableCell colSpan={7}><CircularProgress size={18} /></TableCell></TableRow>
              ) : page.items.map((title) => (
                <TableRow key={title.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {title.name}
                      </Typography>
                      {title.authors.length > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          {title.authors.join(', ')}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={getTypeLabel(title.type)} color={getTypeColor(title.type) as any} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatFileSize(title.file.sizeBytes)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {title.file.mimeType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{products.find((product) => product.id === title.productId)?.titulo}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={title.isActive ? 'Active' : 'Inactive'} color={title.isActive ? 'success' : 'default'} size="small" />
                    {title.durationSec && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {Math.round(title.durationSec / 60)}min
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(title.createdAt)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={canManage ? 'Editar' : 'Você não tem permissão de edição no módulo Títulos.'}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Editar título ${title.name}`}
                          onClick={() => handleOpenDialog(title)}
                          color="primary"
                          disabled={!canManage || deletingTitleId === title.id}
                        >
                          <EditIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={canManage ? 'Excluir' : 'Você não tem permissão de edição no módulo Títulos.'}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Excluir título ${title.name}`}
                          onClick={() => handleOpenDeleteDialog(title)}
                          color="error"
                          disabled={!canManage || deletingTitleId === title.id}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Typography>{page.totalCount} Título(s)</Typography>
        <Box>
          <Button disabled={page.page === 0 || page.loading} onClick={page.previous}>Anterior</Button>
          <Button disabled={!page.nextCursor || page.loading} onClick={page.next}>Próxima</Button>
        </Box>
      </Box>

      {/* Dialog para editar título */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Editar Título</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Nome do Título"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              placeholder="Ex: A Arte da Guerra"
              disabled={submitting}
            />

            {editingTitle?.type === 'ebook' && (
              <TextField
                fullWidth
                label="Número de Páginas"
                type="number"
                value={formData.pages}
                onChange={(e) => setFormData({ ...formData, pages: e.target.value })}
                margin="normal"
                placeholder="Ex: 250"
                disabled={submitting}
              />
            )}

            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={formData.tags}
              onChange={(event, newValue) => {
                setFormData({ ...formData, tags: newValue });
              }}
              disabled={submitting}
              renderTags={(value, getTagProps) => value.map((option, index) => <Chip variant="outlined" label={option} {...getTagProps({ index })} />)}
              renderInput={(params) => <TextField {...params} label="Tags do Título" margin="normal" placeholder="Digite uma tag e pressione Enter" />}
            />

            <TextField
              fullWidth
              label="Descrição"
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              placeholder="Descrição do título..."
              disabled={submitting}
            />

            <CoverUpload currentCoverUrl={formData.coverUrl} onCoverChange={handleCoverChange} disabled={submitting} />

            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={formData.authors}
              onChange={(event, newValue) => {
                setFormData({ ...formData, authors: newValue });
              }}
              disabled={submitting}
              renderTags={(value, getTagProps) => value.map((option, index) => <Chip variant="outlined" label={option} {...getTagProps({ index })} />)}
              renderInput={(params) => <TextField {...params} label="Autores" margin="normal" placeholder="Digite um autor e pressione Enter" />}
            />

            <Autocomplete
              options={publishers}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
              value={publishers.find((p) => p.id === formData.publisherId) ?? null}
              onChange={(_, newValue) => {
                setFormData({ ...formData, publisherId: newValue ? newValue.id : '' });
              }}
              disabled={submitting}
              renderInput={(params) => <TextField {...params} label="Editora" margin="normal" placeholder="Selecione a editora" />}
            />

            <FormControl fullWidth margin="normal" disabled={submitting}>
              <InputLabel id="title-product-label">Ativo</InputLabel>
              <Select
                labelId="title-product-label"
                label="Ativo"
                value={selectedProductId}
                onChange={(event) => setFormData({ ...formData, productId: event.target.value })}
              >
                <MenuItem value="">Nenhum</MenuItem>
                {eligibleProducts.map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                    {product.titulo}
                    {!product.isActive && ' (inativo)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  disabled={submitting}
                />
              }
              label="Título Ativo"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? 'Salvando…' : 'Atualizar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(titlePendingDeletion)} onClose={handleCloseDeleteDialog} aria-labelledby="delete-title-dialog-title">
        <DialogTitle id="delete-title-dialog-title">Excluir título</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o título &quot;{titlePendingDeletion?.name}&quot;?
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={deleteConfirmed}
                onChange={(event) => setDeleteConfirmed(event.target.checked)}
                disabled={Boolean(deletingTitleId)}
              />
            }
            label="Confirmo que desejo excluir este título"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={Boolean(deletingTitleId)}>
            Cancelar
          </Button>
          <Button
            color="error"
            onClick={handleDelete}
            variant="contained"
            disabled={!deleteConfirmed || Boolean(deletingTitleId)}
            startIcon={deletingTitleId ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {deletingTitleId ? 'Excluindo…' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificações */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TitlesPage;
